import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';
import BaseFormCard from '@/views/base/BaseFormCard.vue';
import { commitAddNotification } from '@/store/main/mutations';
import { store } from '@/store/index';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';
import { parseCSV, csvItem } from '@/utils/csvValidation';
import {
  MissingRequiredCsvColumnsError,
  FileCannotReadbleError,
  InvalidFileTypeError,
  NoFileSelectedError,
} from '@/utils/error';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsBulkOperationsDialog',
  props: {
    value: { type: Boolean },
    isDownloadable: { type: Boolean },
    isUploadable: { type: Boolean },
    totalChangedNumber: null,
    uploadProcessNumber: null,
    fetchSdsesProcessString: null,
    header: null,
    uploadLimitText: null,
    downloadCsvText: null
  },
  emits: ["input", "downloadCSV", "csvFileValidate"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'input', value: boolean): void;
  (e: 'downloadCSV'): void;
  (
    e: 'csvFileValidate',
    csvDataArray: csvItem[],
    hideParentModal: boolean
  ): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: boolean;
  isDownloadable: boolean;
  isUploadable: boolean;
  totalChangedNumber: number;
  uploadProcessNumber: number;
  fetchSdsesProcessString: string;
  header: string;
  uploadLimitText: string;
  downloadCsvText: string;
};

const m = defineMessages({
  csvValidationProcess: '検証中({uploadCount})/合計({totalCount})',
});





const model = computed({
  get: () => props.value,
  set: (value) => emit('input', value),
});

const csvFile = ref<File | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const isValidFile = ref<boolean>(true);
const uploadProgress = ref<number>(0);
const parseCSVData = ref<csvItem[]>([]);
const showValidationProcess = ref<boolean>(false);

watch(
  () => model.value,
  (newValue) => {
    if (newValue) {
      csvFile.value = null;
    }
  }
);

const closeDialog = () => {
  csvFile.value = null;
  model.value = false;
  showValidationProcess.value = false;
};

const handleCancelClick = () => {
  closeDialog();
};

const handleDownloadClick = () => {
  emit('downloadCSV');
};

const triggerFileInput = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  } else {
    commitAddNotification(store, {
      messageKey: 'CSVファイル以外アップロード不可にする。',
      color: 'error',
    });
  }
};

const handleFileUpload = async (e: Event | DragEvent) => {
  try {
    const target = e.target as HTMLInputElement;
    if (e instanceof DragEvent && e.dataTransfer?.files.length) {
      csvFile.value = e.dataTransfer.files[0];
    } else if (target.files && target.files.length > 0) {
      csvFile.value = target.files[0];
    }
    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }
    if (csvFile.value) {
      uploadProgress.value = 0;
      isValidFile.value = true;

      // 1. Check csv file extension
      if (!csvFile.value.name.toLowerCase().endsWith('.csv')) {
        throw new InvalidFileTypeError('Only CSV files are allowed');
      }

      // 2. Parse CSV file and check that there is no error
      const parsedData = await parseCSV(csvFile.value);
      if (parsedData) {
        uploadProgress.value = 100;
        parseCSVData.value = parsedData;
      }
    } else {
      commitAddNotification(store, {
        messageKey: 'No file selected!',
        color: 'error',
      });
    }
  } catch (error) {
    isValidFile.value = false;

    if (fileInputRef.value) {
      fileInputRef.value.value = '';
    }

    const catchedError =
      error instanceof Error
        ? error
        : new Error('An unknown error occurred during file upload');

    notifyErrorWhenUploadingFile(catchedError);
  }
};

const notifyErrorWhenUploadingFile = (error: Error) => {
  if (error instanceof InvalidFileTypeError) {
    commitAddNotification(store, {
      messageKey:
        'CSVファイル以外アップロードできません。ファイルの拡張子が.csvであることを確認して、再度アップロードしてください。',
      color: 'error',
    });

    return;
  }

  if (error instanceof FileCannotReadbleError) {
    commitAddNotification(store, {
      messageKey:
        'CSVファイルの内容を読み取れませんでした。しばらくしてから再度アップロードしてください。',
      color: 'error',
    });

    return;
  }

  if (error instanceof NoFileSelectedError) {
    commitAddNotification(store, {
      messageKey: 'No file selected!',
      color: 'error',
    });

    return;
  }

  if (error instanceof MissingRequiredCsvColumnsError) {
    commitAddNotification(store, {
      messageKey: `CSVファイルに次のヘッダーが不足しています: [ ${error.columns.join(
        ', '
      )} ]`,
      color: 'error',
    });

    return;
  }

  commitAddNotification(store, {
    messageKey:
      'CSVファイルアップロードに失敗しました。しばらくしてから再度アップロードしてください。',
    color: 'error',
  });

  return;
};

const handleExecuteClick = () => {
  if (parseCSVData.value) {
    showValidationProcess.value = true;
    emit('csvFileValidate', parseCSVData.value, true);
  }
};

const removeCSVFile = () => {
  csvFile.value = null;
  showValidationProcess.value = false;
  uploadProgress.value = 0;
};

return { __sfc: true,m, props, emit, model, csvFile, fileInputRef, isValidFile, uploadProgress, parseCSVData, showValidationProcess, closeDialog, handleCancelClick, handleDownloadClick, triggerFileInput, handleFileUpload, notifyErrorWhenUploadingFile, handleExecuteClick, removeCSVFile, BaseFormCard, sharedMessages }
}

})